/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  withAssign,
  type Action,
  type Atom,
} from '@reatom/framework'

import { AsyncStateN } from '@blade/std-lib'
import { Trend } from '@root/entities/trend'
import { PnlChartDivision } from '@root/features/portfolio/user-pnl-chart/model'

import { UserTradingAnalyticsPeriod } from '../models/user-trading-analytics-period'
import { UserTradingAnalyticsPnlView } from '../models/user-trading-analytics-pnl-view'
import { UserTradingAnalyticsPriceType } from '../models/user-trading-analytics-price-type'

import { reatomUserTradingAnalytics as reatomUserTradingAnalyticsResource } from './reatom-user-trading-analytics'
import { reatomUserTradingProfilePnlCalendar } from './user-trading-analytics-pnl-calendar'

import type { UserTradingAnalytics } from '../models/user-trading-analytics'
import type { ProfileWallet } from '@root/entities/profile/model/profile-wallet'
import type { DelugeId } from '@root/entities/user-short'
import type { Error } from '@root/shared'

export const PeriodToDivisionMap: Record<
  UserTradingAnalyticsPeriod,
  PnlChartDivision.PnlChartDivision
> = {
  [UserTradingAnalyticsPeriod.ALL]: new PnlChartDivision.AsIs(),
  [UserTradingAnalyticsPeriod['7D']]: new PnlChartDivision.Day({ number: 7 }),
  [UserTradingAnalyticsPeriod['1D']]: new PnlChartDivision.Day({ number: 1 }),
  [UserTradingAnalyticsPeriod['12H']]: new PnlChartDivision.Hour({
    number: 12,
  }),
  [UserTradingAnalyticsPeriod['6H']]: new PnlChartDivision.Hour({
    number: 6,
  }),
  [UserTradingAnalyticsPeriod['1H']]: new PnlChartDivision.FifteenMinute({
    number: 4,
  }),
}

export const ALL_WALLETS = 'ALL_WALLETS'

export const userTradingAnalyticsPeriodAtom = atom<UserTradingAnalyticsPeriod>(
  UserTradingAnalyticsPeriod['7D'],
  'userTradingAnalyticsPeriodAtom',
)

export const reatomUserTradingAnalytics = (
  args: {
    delugeId: DelugeId
    wallets: ReadonlyArray<ProfileWallet>
    /**
     * Where the figures come from, when it is not the signed-in account.
     *
     * Everything below this line is glue — it turns one `UserTradingAnalytics`
     * into the period tabs, the chart model, the calendar and the trend — and
     * none of it cares who the analytics are about. Handing the source in is
     * what lets a wallet address drive the same screen; see
     * `reatomWalletTradingAnalytics`.
     */
    source?:
      | (Atom<
          AsyncStateN.AsyncStateN<Error.UnknownError, UserTradingAnalytics>
        > & { refresh: Action })
      /**
       * Or a source built from the wallets the selector has resolved.
       *
       * The selector lives inside this model, so a source that depends on it
       * cannot be created before it. Handing in a factory instead of an atom
       * is what lets the signed-in account's own profile read from the wallet
       * profile endpoint, one wallet or all of them.
       */
      | ((
          walletsAtom: Atom<ReadonlyArray<ProfileWallet>>,
        ) => Atom<
          AsyncStateN.AsyncStateN<Error.UnknownError, UserTradingAnalytics>
        > & { refresh: Action })
  },
  _name: string,
) => {
  const dateAtom = atom(new Date(), 'dateAtom')
  const selectedWalletsAtom = atom<
    ReadonlyArray<ProfileWallet> | typeof ALL_WALLETS
  >(ALL_WALLETS, 'walletAddressesAtom')

  const periodAtom = userTradingAnalyticsPeriodAtom
  const priceTypeAtom = atom<UserTradingAnalyticsPriceType>(
    UserTradingAnalyticsPriceType.USD,
    'priceTypeAtom',
  )
  const pnlViewAtom = atom<UserTradingAnalyticsPnlView>(
    UserTradingAnalyticsPnlView.CALENDAR,
    'userTradingAnalyticsPnlViewAtom',
  )

  const walletsResolvedAtom = atom((ctx) => {
    const selectedWallets = ctx.spy(selectedWalletsAtom)
    if (selectedWallets === ALL_WALLETS) {
      return args.wallets
    }
    return selectedWallets
  })

  const requestStateAtom =
    (typeof args.source === 'function'
      ? args.source(walletsResolvedAtom)
      : args.source) ??
    reatomUserTradingAnalyticsResource({
      delugeId: args.delugeId,
      selectedWalletsAtom: walletsResolvedAtom,
      periodAtom: periodAtom,
      wallets: args.wallets,
    })

  const pnlViewModelAtom = atom((ctx) => {
    const requestState = ctx.spy(requestStateAtom)

    return AsyncStateN.map(requestState, (data) => {
      return data.daily.map((day) => ({
        timestamp: day.date,
        pnl: day.pnlUsd,
      }))
    })
  })

  const pnlRequestAtom = atom((ctx) => {
    const requestState = ctx.spy(requestStateAtom)

    return AsyncStateN.map(requestState, (data) => {
      return data.daily.map((x) => ({
        timestamp: x.date,
        pnl: x.pnlUsd,
        pnlNative: x.pnlUsd,
        pnlUsd: x.pnlUsd,
      }))
    })
  })

  const trendAtom = atom((ctx) => {
    const requestState = ctx.spy(requestStateAtom)
    const trend = Trend.of(
      requestState.value?.performance.realizedPnlUsd.toNumber() ?? 0,
    )

    // If the trend is not neutral, return the trend
    if (trend !== Trend.Neutral) {
      return trend
    }

    // If the trend is neutral, return the last pnl from the daily data
    return Trend.of(requestState.value?.daily.at(-1)?.pnlUsd ?? 0)
  })

  const retryAction = action(
    (ctx) => requestStateAtom.refresh(ctx),
    'retryAction',
  )

  const divisionAtom = atom(
    (ctx) => PeriodToDivisionMap[ctx.spy(periodAtom)],
    'divisionAtom',
  )

  const pnlChartModelAtom = pnlRequestAtom.pipe(
    withAssign(() => ({
      period: periodAtom,
      date: dateAtom,
      retryAction,
      division: divisionAtom,
    })),
  )

  const calendarModel = reatomUserTradingProfilePnlCalendar({
    dataAtom: pnlRequestAtom,
    dateAtom,
    retryAction,
  })

  return {
    selectedWalletsAtom,
    wallets: args.wallets,
    delugeId: args.delugeId,
    pnlChartModelAtom,
    requestStateAtom,
    periodAtom,
    priceTypeAtom,
    pnlViewAtom,
    trendAtom,
    pnlViewModelAtom,
    calendarModel,
  }
}

export type UserTradingAnalyticsModel = ReturnType<
  typeof reatomUserTradingAnalytics
>
